import {
    globalRef,
    isSilentError,
    logError,
    logWarn,
    onLine,
    RequestError,
    RequestFailureType,
    signal,
    type LogTarget,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { type MutableValue } from '@amazon/vinyl-observable'
import { redactJson, redactText } from '@/redactSensitive'

const errorHandlerTarget: LogTarget = {
    logPrefix: 'errorHandler',
}

export type ErrorReport = { target: LogTarget; error: unknown }

/**
 * Formats an unknown catch/reject value into one readable log string: the
 * `stack` (message + trace) for an `Error`, JSON for a plain object, else
 * `String(err)`. A raw `Error` handed straight to the logger serializes to an
 * opaque `[object Object]` once the console bridge / minified bundle stringifies
 * it; this keeps the message and trace legible. Lives here —
 * the lowest error layer — so both `handleError` and util-level loggers can
 * reach it without a `errorHandler → util` back-edge.
 */
export function formatError(err: unknown): string {
    if (err instanceof Error) return redactText(err.stack ?? `${err.name}: ${err.message}`)
    if (typeof err === 'object' && err !== null) {
        try {
            return JSON.stringify(redactJson(err))
        } catch {
            return `{${Object.keys(err).join(', ')}}`
        }
    }
    return redactText(String(err))
}

// Errors are broadcast on a signal rather than errorHandler reaching into the
// UI layer: errorHandler is foundational (every `ctx()` builds one) while the
// toast pulls in `@/components/toast` → Icon → back into `ctx`, so a direct
// import would form a module-eval cycle. Consumers (the debug-gated toast and
// the bug-report diagnostics capture) subscribe via `onError` at bootstrap.
const errorSignal = signal<ErrorReport>()

/** Subscribe to non-silent errors routed through `handleError`. */
export function onError(cb: (report: ErrorReport) => void): Unsubscribe {
    return errorSignal.listen(cb)
}

// A fetch that fails at the transport while the device is offline is expected, not a fault (GH
// only logs it); the error itself is untouched so its surface can still show the offline state.
function isOfflineNetworkFailure(error: unknown): boolean {
    return (
        error instanceof RequestError && error.info.type === RequestFailureType.NETWORK && !onLine()
    )
}

const handledErrorsRef = globalRef(() => new WeakSet<object>())

// One failure is one report: a path that handles an error and rethrows it, or toasts it after
// another layer handled it, must not send it twice.
function isAlreadyHandled(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) return false
    const handledErrors = handledErrorsRef.value
    if (handledErrors.has(error)) return true
    handledErrors.add(error)
    return false
}

export function handleError(target: LogTarget, error: unknown): void {
    if (isSilentError(error)) return
    if (isOfflineNetworkFailure(error)) {
        logWarn(target, `offline: ${formatError(error)}`)
        return
    }
    if (isAlreadyHandled(error)) return
    logError(target, formatError(error))
    errorSignal.dispatch({ target, error })
}

export function createErrorHandler(target: LogTarget): (error: unknown) => void {
    return (error) => handleError(target, error)
}

const SURFACED_WINDOW_MS = 30_000
const surfacedErrorsRef = globalRef(() => new WeakSet<object>())
const surfacedTextsRef = globalRef(() => new Map<string, number>())

/**
 * Claims the right to show the user a message about `error`, returning false
 * when another surface already showed this failure — a lazy component's inline
 * error element beside the global error toast, two dynamic imports of one broken
 * module rejecting separately, a retried request failing the same way. Matching
 * is by error instance and by `text`, the message about to be shown (defaulting
 * to the `Error`'s own message), so two failures that read identically count as
 * one incident. Every user-visible error surface claims before it paints, and a
 * recurrence after the window has elapsed is a new incident worth surfacing.
 */
export function claimErrorSurface(error: unknown, text?: string): boolean {
    if (typeof error === 'object' && error !== null) {
        const surfacedErrors = surfacedErrorsRef.value
        if (surfacedErrors.has(error)) return false
        surfacedErrors.add(error)
    }
    const shownText = text ?? (error instanceof Error ? error.message : null)
    if (shownText == null) return true
    const now = performance.now()
    const surfacedTexts = surfacedTextsRef.value
    const last = surfacedTexts.get(shownText)
    if (last != null && now - last < SURFACED_WINDOW_MS) return false
    surfacedTexts.set(shownText, now)
    // Opportunistic eviction so the map can't grow without bound when many
    // distinct messages flow through.
    for (const [key, at] of surfacedTexts) {
        if (now - at >= SURFACED_WINDOW_MS) surfacedTexts.delete(key)
    }
    return true
}

/**
 * `.catch` handler for a detail-page section fetch: logs + captures the error
 * and stores it on `err$` so the section's own inline surface (retry row)
 * paints. Claims the failure so nothing else repeats it — the section's inline
 * surface is the user-visible message.
 */
export function reportToInline(
    target: LogTarget,
    err$: MutableValue<Error | null>
): (error: Error) => void {
    return (error) => {
        claimErrorSurface(error)
        handleError(target, error)
        err$.value = error
    }
}

export function initializeGlobalErrorHandling(): void {
    // Use raw addEventListener; errorHandler is the lowest layer (global error
    // capture) and must not depend on util to avoid a module cycle.
    window.addEventListener('error', (event) => {
        if (!(event.error instanceof Error)) return
        handleError(errorHandlerTarget, event.error)
    })
    window.addEventListener('unhandledrejection', (event) => {
        handleError(errorHandlerTarget, event.reason)
    })
}
